import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Flame, Loader2, MessageSquare } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionLock } from '@/components/LockedFeatureSheet';
import { useRecoveryLog } from '@/components/RecoveryTiles';
import { RecoveryChart } from '@/components/RecoveryChart';

const BLUE = '#3B82F6';
const RED = '#D1494B';
const LIME = 'var(--accent-hex)';

const METRICS = [
  { key: 'water_ml', label: 'Water', short: 'Water', color: BLUE, img: '/recovery-hydrate.jpg?v=3', daily: true, pillar: 'hydrate' },
  { key: 'protein_g', label: 'Protein', short: 'Protein', color: RED, img: '/recovery-refuel.jpg?v=3', daily: true, pillar: 'refuel' },
  { key: 'calories', label: 'Calories', short: 'Calories', color: RED, img: '/recovery-refuel.jpg?v=3', daily: true, pillar: 'refuel' },
  { key: 'carbs_g', label: 'Carbs', short: 'Carbs', color: RED, img: '/recovery-refuel.jpg?v=3', daily: true, pillar: 'refuel' },
  { key: 'fat_g', label: 'Fat', short: 'Fat', color: RED, img: '/recovery-refuel.jpg?v=3', daily: true, pillar: 'refuel' },
  { key: 'sleep_hours', label: 'Sleep', short: 'Sleep', color: LIME, img: '/recovery-sleep.jpg?v=3', daily: false, pillar: 'sleep' },
  { key: 'recovery_score', label: 'Recovery score', short: 'Recovery', color: LIME, img: null, daily: false, pillar: null },
];

const STATUS_COLOR = {
  achieved: LIME, in_range: LIME, short: '#F0A33C', below_range: '#F0A33C',
  over: '#F0A33C', above_range: '#E5484D', assumed: '#8A9099', not_recorded: '#8A9099',
};

const darken = (hex, amt = 0.5) => {
  const n = parseInt(hex.slice(1), 16);
  const c = (v) => Math.round(v * (1 - amt));
  return `rgb(${c((n >> 16) & 255)},${c((n >> 8) & 255)},${c(n & 255)})`;
};

const fmt = (value, unit) => {
  if (value === null || value === undefined) return '—';
  if (unit === 'ml') return value >= 1000 ? `${(value / 1000).toFixed(2)} L` : `${Math.round(value)} ml`;
  if (unit === 'h') return `${Math.round(value * 10) / 10}h`;
  if (unit === 'kcal') return `${Math.round(value)}`;
  if (unit === 'pts') return `${Math.round(value)}`;
  return `${Math.round(value)}g`;
};
const fmtAxis = (value, unit) => {
  if (unit === 'ml') return `${(value / 1000).toFixed(1)}L`;
  if (unit === 'h') return `${Math.round(value)}h`;
  return `${Math.round(value)}`;
};
const dayLabel = (date, range) => {
  const d = new Date(`${date}T00:00:00`);
  if (range === 'month') return d.getDate() % 5 === 0 ? `${d.getDate()}` : '';
  return d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 3);
};

const Bar = ({ pct, color }) => (
  <span className="block h-2 rounded-full bg-white/10 overflow-hidden">
    <span className="block h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(pct || 0, 2))}%`, background: color }} />
  </span>
);

const SummaryRow = ({ metric, status, rule, onTap }) => (
  <button onClick={onTap} className="w-full flex items-center gap-3 py-2 active:scale-[0.99] transition-transform"
          data-testid={`analytics-summary-${metric.key}`}>
    {metric.img && <img src={metric.img} alt="" className="w-14 h-11 rounded-lg object-cover shrink-0" loading="lazy" />}
    <span className="flex-1 min-w-0 text-left">
      <span className="block font-heading font-black uppercase tracking-tight text-white text-base">{metric.label}</span>
      <span className="block text-[13px] text-white/45">
        {status.value === null ? 'Not recorded' : `${fmt(status.value, rule.unit)} / ${fmt(rule.target, rule.unit)}`}
      </span>
    </span>
    <span className="w-[34%] shrink-0">
      <Bar pct={status.value === null ? 0 : status.pct} color={metric.color} />
      <span className="mt-1 block text-[12px] font-heading font-bold uppercase tracking-wide text-right"
            style={{ color: STATUS_COLOR[status.status] }} data-testid={`analytics-status-${metric.key}`}>
        {status.headline}
      </span>
    </span>
  </button>
);

export const RecoveryAnalyticsPanel = ({ open, onOpenChange }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { unlocked } = useSubscriptionLock();
  const log = useRecoveryLog();
  const [range, setRange] = useState('week');
  const [metricKey, setMetricKey] = useState('water_ml');
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchRange = range === 'month' ? 'month' : 'week';

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await api.get(`/recovery/analytics?range=${fetchRange}&demo=${unlocked ? 'auto' : '1'}`, {
        headers: { 'X-TZ-Offset': String(new Date().getTimezoneOffset()) },
      });
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api, fetchRange, unlocked]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // A log made from inside the panel refreshes these numbers straight away.
  useEffect(() => {
    if (!open) return undefined;
    const onChanged = () => load();
    window.addEventListener('recovery:changed', onChanged);
    return () => window.removeEventListener('recovery:changed', onChanged);
  }, [open, load]);

  // Android back / browser back closes the panel instead of leaving the page.
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ recoveryAnalytics: true }, '');
    const onPop = () => onOpenChange(false);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.recoveryAnalytics) window.history.back();
    };
  }, [open, onOpenChange]);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const rule = data?.rules?.[metric.key] || { unit: metric.key === 'recovery_score' ? 'pts' : 'g', target: 0, label: metric.label };
  const effectiveRange = metric.daily ? range : (range === 'day' ? 'week' : range);

  // Today's running total through the day — no extra request, built from today's entries.
  const points = useMemo(() => {
    if (!data) return [];
    if (effectiveRange !== 'day') return data.series[metric.key] || [];
    const entries = metric.key === 'water_ml'
      ? (data.today.water_entries || []).map((e) => ({ at: e.at, add: e.ml }))
      : (data.today.meal_entries || []).map((e) => ({ at: e.at, add: e[metric.key] || 0 }));
    let running = 0;
    return [{ date: 'start', value: 0, source: 'logged', status: 'logged' },
      ...entries.map((e) => { running += e.add; return { date: e.at, value: running, source: 'logged', status: 'logged' }; })];
  }, [data, metric.key, effectiveRange]);

  const selectedIndex = selected === null ? points.length - 1 : Math.min(selected, points.length - 1);
  const point = points[selectedIndex];
  const target = metric.key === 'recovery_score' ? 0 : rule.target;

  const tooltip = useMemo(() => {
    if (!point || point.value === null || point.value === undefined) return null;
    const pointTarget = point.target ?? target;
    const diff = point.value - pointTarget;
    let status = '';
    if (metric.key === 'recovery_score') status = `${point.days_counted || 0} days of data`;
    else if (point.source === 'auto') status = 'Assumed, not logged';
    else if (rule.direction === 'max') status = diff <= 0 ? 'Within target ✓' : `${fmt(diff, rule.unit)} above target`;
    else if (point.status === 'in_range') status = 'On target ✓';
    else if (diff >= 0) status = 'Goal Achieved ✓';
    else status = `${fmt(-diff, rule.unit)} to go`;
    const d = effectiveRange === 'day' || point.date === 'start'
      ? 'Today'
      : new Date(`${point.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
    return { title: d, status, statusColor: STATUS_COLOR[point.status] || '#9CA3AF' };
  }, [point, target, rule, metric.key, effectiveRange]);

  // Hand a shortfall straight to the coach — it already sees today's gap and their profile.
  const askCoach = (key, status) => {
    const r = data?.rules?.[key];
    if (!r) return;
    const name = r.label.toLowerCase();
    const q = (status.status === 'above_range' || status.status === 'over')
      ? `I'm ${fmt(status.excess, r.unit)} over my ${name} target today — how should I handle the rest of the day?`
      : status.status === 'not_recorded'
        ? `I haven't logged any ${name} today — what should I aim for?`
        : `I'm ${fmt(status.remaining, r.unit)} short of my ${name} target today — how do I close it?`;
    onOpenChange(false);
    navigate(`/chat?ask=${encodeURIComponent(q)}`);
  };


  const today = data?.today;
  const summaryMetrics = ['protein_g', 'water_ml', 'sleep_hours'];
  const periodSummary = data?.summary?.[metric.key];
  const scoreDays = (data?.series?.recovery_score || []).filter((p) => p.value !== null).length;
  const targetChanges = points.filter((p) => p.change).slice(-2);
  const hasPlot = points.length > 1 && points.some((p) => p.value !== null && p.value !== undefined);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="bottom"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="h-[100dvh] max-h-[100dvh] w-full p-0 bg-[#07090C] border-0 rounded-none overflow-y-auto"
        data-testid="recovery-analytics-panel">
        <div
          className="px-4"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 7rem)',
          }}
        >
          <div className="flex items-center gap-2 pr-10">
            <button onClick={() => onOpenChange(false)}
              className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center bg-white/[0.06] text-white active:scale-90 transition-transform"
              aria-label="Close analytics" data-testid="analytics-close-btn">
              <ChevronLeft size={20} />
            </button>
            <h2 className="font-heading font-black uppercase tracking-tight text-white text-lg">Recovery analytics</h2>
            {data?.streak?.current > 0 && (
              <span className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-heading font-black uppercase"
                    style={{ border: '1px solid rgba(10,132,255,0.5)', background: 'rgba(10,132,255,0.14)', color: '#9CCBFF' }}
                    data-testid="analytics-streak">
                <Flame className="w-3.5 h-3.5" />{data.streak.current}d
              </span>
            )}
          </div>

          {loading && !data && (
            <div className="h-[60vh] flex items-center justify-center" data-testid="analytics-loading">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          )}
          {error && !data && (
            <div className="h-[40vh] flex flex-col items-center justify-center gap-3" data-testid="analytics-error">
              <p className="text-base text-white/50">Couldn't load your analytics.</p>
              <button onClick={load} className="px-4 py-2 rounded-xl bg-white/10 text-white text-base font-heading font-bold">Try again</button>
            </div>
          )}

          {data && (
            <>
              {/* Today's progress */}
              <div className="mt-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="analytics-today-card">
                <p className="text-[13px] uppercase tracking-[0.14em] text-white/45">Today's progress</p>
                <div className="flex items-baseline gap-3">
                  <span className="font-heading font-black text-white text-5xl" data-testid="analytics-targets-pct">
                    {today.targets_met_pct}%
                  </span>
                  <span className="text-sm text-white/50">
                    {today.goals_met} of {today.goals_total} daily targets met
                  </span>
                </div>

                <div className="mt-3 divide-y divide-white/[0.06]">
                  {summaryMetrics.map((key) => {
                    const m = METRICS.find((x) => x.key === key);
                    return (
                      <SummaryRow key={key} metric={m} status={today.metrics[key]} rule={data.rules[key]}
                                  onTap={() => { setMetricKey(key); setSelected(null); }} />
                    );
                  })}
                </div>

                <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="text-sm text-white/55">
                    Recovery score:{' '}
                    <span className="font-heading font-black text-white text-base" data-testid="analytics-score">
                      {today.score === null ? '—' : `${today.score}/100`}
                    </span>
                    {today.score_vs_average !== null && today.score !== null && (
                      <span className="ml-1 text-[13px]" style={{ color: today.score_vs_average >= 0 ? LIME : '#F0A33C' }}>
                        ({today.score_vs_average >= 0 ? '+' : ''}{today.score_vs_average} vs avg)
                      </span>
                    )}
                  </span>
                  <span className="text-[13px] font-heading font-bold uppercase" style={{ color: LIME }}>
                    {today.goals_met === today.goals_total ? 'All done' : 'On track'}
                  </span>
                </div>
                {data.is_demo && (
                  <p className="mt-3 text-[13px] text-white/35" data-testid="analytics-sample-note">
                    Sample week shown against your own targets — log today and your real numbers take over.
                  </p>
                )}
              </div>

              {/* Timeframe */}
              <div className="mt-4 grid grid-cols-3 gap-1 p-1 rounded-2xl bg-white/[0.04]" data-testid="analytics-range-tabs">
                {[['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']].map(([key, label]) => (
                  <button key={key} onClick={() => { setRange(key); setSelected(null); }}
                    className={`py-2 rounded-xl font-heading font-bold text-base transition-colors ${
                      range === key ? 'bg-white/[0.12] text-white' : 'text-white/45'}`}
                    data-testid={`analytics-range-${key}`}>{label}</button>
                ))}
              </div>

              {/* Metric chips */}
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="analytics-metric-chips">
                {METRICS.map((m) => (
                  <button key={m.key} onClick={() => { setMetricKey(m.key); setSelected(null); }}
                    className={`shrink-0 px-3 py-2 rounded-xl border font-heading font-bold text-sm transition-colors ${
                      m.key === metricKey ? 'text-white' : 'text-white/50 border-white/10 bg-white/[0.03]'}`}
                    style={m.key === metricKey
                      ? { borderColor: `${m.color}80`, background: `${m.color}1F`, color: '#fff' } : undefined}
                    data-testid={`analytics-metric-${m.key}`}>{m.short}</button>
                ))}
              </div>

              {/* Chart */}
              <div className="mt-3 rounded-2xl border border-white/[0.08] bg-[#0B0D10] p-3" data-testid="analytics-chart-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] uppercase tracking-[0.14em] text-white/45">{rule.label}</p>
                    <p className="font-heading font-black text-white text-3xl leading-tight" data-testid="analytics-chart-reading">
                      {metric.key === 'recovery_score'
                        ? (today.score === null ? '—' : `${today.score}`)
                        : fmt(effectiveRange === 'day'
                          ? today.metrics[metric.key]?.value
                          : periodSummary?.average, rule.unit)}
                      {rule.unit === 'kcal' && <span className="text-base text-white/40"> kcal</span>}
                    </p>
                    <p className="text-[13px] text-white/40" data-testid="analytics-chart-subtext">
                      {metric.key === 'recovery_score'
                        ? `Trailing 7-day score, ${scoreDays} day${scoreDays === 1 ? '' : 's'} with data`
                        : effectiveRange === 'day' ? 'Today, running total'
                          : `${effectiveRange === 'month' ? '30-day' : '7-day'} average of ${periodSummary?.logged_days ?? 0} logged ${periodSummary?.logged_days === 1 ? 'day' : 'days'}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {target > 0 && (
                      <p className="text-[13px] text-white/45">
                        <span className="inline-block w-5 border-t border-dashed border-white/50 align-middle mr-1" />
                        Target: {fmt(target, rule.unit)}
                      </p>
                    )}
                    {effectiveRange !== 'day' && periodSummary && (
                      <p className="mt-1 inline-block px-2 py-1 rounded-lg text-[12px] font-heading font-bold"
                         style={{ background: 'rgba(var(--accent-rgb),0.12)', color: LIME }}
                         data-testid="analytics-hit-days">
                        Target hit {periodSummary.target_hit_days}/{periodSummary.logged_days || 0} logged
                      </p>
                    )}
                  </div>
                </div>

                {!hasPlot ? (
                  <div className="h-[200px] flex flex-col items-center justify-center gap-2" data-testid="analytics-chart-empty">
                    <p className="text-base text-white/40">
                      {effectiveRange === 'day' ? `Nothing logged today yet.` : 'Nothing logged for this view yet.'}
                    </p>
                    {metric.pillar && (
                      <button onClick={() => log.open(metric.pillar)}
                        className="px-4 py-2 rounded-xl text-base font-heading font-bold text-white"
                        style={{ background: metric.color }} data-testid="analytics-empty-log-btn">
                        Log {metric.label.toLowerCase()}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    <RecoveryChart
                      points={points} target={target} color={metric.color}
                      selected={selectedIndex} onSelect={setSelected}
                      formatValue={(v) => fmt(v, rule.unit)}
                      formatAxis={(v) => fmtAxis(v, rule.unit)}
                      formatLabel={(date, i) => (effectiveRange === 'day'
                        ? (date === 'start' ? '' : new Date(date).toLocaleTimeString(undefined, { hour: 'numeric' }))
                        : dayLabel(date, effectiveRange))}
                      tooltip={tooltip}
                    />
                  </div>
                )}
                {!metric.daily && range === 'day' && (
                  <p className="mt-1 text-[13px] text-white/35" data-testid="analytics-daily-note">
                    {rule.label} is recorded once a day, so this shows the week.
                  </p>
                )}
                {targetChanges.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="analytics-target-changes">
                    {targetChanges.map((c) => (
                      <p key={c.date} className="text-[13px] text-white/45 flex items-start gap-1.5">
                        <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-white shrink-0" />
                        <span>
                          Target moved {new Date(`${c.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                          : {fmt(c.change.from, rule.unit)} → {fmt(c.change.to, rule.unit)} ({c.change.reason})
                        </span>
                      </p>
                    ))}
                  </div>
                )}
                {rule.basis && (
                  <p className="mt-2 text-[13px] text-white/30" data-testid="analytics-basis">{rule.basis}</p>
                )}
                {metric.key !== 'recovery_score' && today.metrics[metric.key]
                  && !['achieved', 'in_range'].includes(today.metrics[metric.key].status) && (
                  <button onClick={() => askCoach(metric.key, today.metrics[metric.key])}
                    className="mt-3 w-full py-3 rounded-xl border border-white/15 bg-white/[0.04] font-heading font-bold text-base text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                    data-testid="analytics-chart-coach-btn">
                    <MessageSquare className="w-4 h-4" style={{ color: metric.color }} />
                    Ask coach how to close today's {rule.label.toLowerCase()}
                  </button>
                )}
              </div>

              {/* What to do next */}
              {data.actions.length > 0 && (
                <div className="mt-4" data-testid="analytics-actions">
                  <p className="text-[13px] uppercase tracking-[0.14em] text-white/45">What's left today</p>
                  <div className="mt-2 space-y-2">
                    {data.actions.map((a) => {
                      const m = METRICS.find((x) => x.key === a.metric);
                      const c = m?.color || '#64748B';
                      return (
                        <div key={a.metric}
                             className="relative overflow-hidden p-3.5 rounded-2xl border border-black/[0.06]"
                             style={{
                               background: 'linear-gradient(145deg,#FFFFFF 0%,#F8FAFD 45%,#E7EDF5 100%)',
                               boxShadow: '0 12px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.95)',
                             }}
                             data-testid={`analytics-action-${a.metric}`}>
                          <span aria-hidden className="absolute inset-y-0 left-0 w-[5px]"
                                style={{ background: `linear-gradient(180deg, ${c} 0%, ${c}55 100%)` }} />
                          <span aria-hidden className="pointer-events-none absolute inset-0"
                                style={{ background: `linear-gradient(100deg, ${c}1C 0%, ${c}0A 20%, rgba(255,255,255,0) 46%)` }} />
                          <div className="relative pl-2.5">
                            <p className="font-heading text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: darken(c) }}>
                              {a.rule?.label || m?.label || 'Recovery'}
                            </p>
                            <p className="mt-1.5 text-[15px] leading-[1.35] font-medium tracking-[-0.01em] text-[#141A21]">
                              {a.action}
                            </p>
                            <div className="mt-3 flex items-center justify-end gap-2">
                              <button onClick={() => askCoach(a.metric, a)}
                                className="shrink-0 h-10 px-3.5 rounded-xl flex items-center gap-1.5 border border-black/[0.08] bg-black/[0.035] text-[#3A4047] font-heading text-[11px] font-black uppercase tracking-[0.12em] active:scale-95 transition-transform"
                                aria-label={`Ask the coach about ${a.rule?.label || a.metric}`}
                                data-testid={`analytics-action-coach-${a.metric}`}>
                                <MessageSquare className="w-3.5 h-3.5" />
                                Ask
                              </button>
                              {m?.pillar && (
                                <button onClick={() => log.open(m.pillar)}
                                  className="relative shrink-0 h-10 px-4 rounded-xl overflow-hidden active:scale-95 transition-transform"
                                  style={{ border: `1px solid ${m.color}99`, boxShadow: `0 4px 14px ${m.color}3D` }}
                                  aria-label={`Log ${m.label.toLowerCase()}`}
                                  data-testid={`analytics-action-log-${a.metric}`}>
                                  {m.img && <img src={m.img} alt="" loading="lazy"
                                                 className="absolute inset-0 w-full h-full object-cover" />}
                                  <span className="absolute inset-0" style={{ background: 'rgba(7,9,12,0.5)' }} />
                                  <span className="relative font-heading text-[11px] font-black uppercase tracking-[0.12em] text-white">Log</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick log */}
              <div className="mt-4" data-testid="analytics-quick-log">
                <p className="text-[13px] uppercase tracking-[0.14em] text-white/45">Quick log</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {['hydrate', 'refuel', 'sleep'].map((pillar) => {
                    const m = METRICS.find((x) => x.pillar === pillar);
                    const key = pillar === 'hydrate' ? 'water_ml' : pillar === 'sleep' ? 'sleep_hours' : 'protein_g';
                    const st = today.metrics[key];
                    return (
                      <button key={pillar} onClick={() => log.open(pillar)}
                        className="relative rounded-2xl overflow-hidden border border-white/[0.08] p-3 text-left active:scale-[0.97] transition-transform"
                        data-testid={`analytics-quick-${pillar}`}>
                        <img src={m.img} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                        <span className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(4,6,10,0.65) 0%, rgba(4,6,10,0.9) 100%)' }} />
                        <span className="relative block">
                          <span className="block text-[12px] uppercase tracking-[0.12em]" style={{ color: m.color }}>{m.label}</span>
                          <span className="block font-heading font-black text-white text-xl leading-tight">
                            {fmt(st.value, data.rules[key].unit)}
                          </span>
                          <span className="block text-[12px] text-white/45">Goal {fmt(data.rules[key].target, data.rules[key].unit)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        {log.sheet}
      </SheetContent>
    </Sheet>
  );
};

export default RecoveryAnalyticsPanel;
